import { useApiClient } from '@yuksalish/api-client'
import type {
  EquipmentItem,
  KioskDailyOutput,
  KioskDailyOutputRow,
  KioskDowntimeReason,
  KioskEmployee,
  KioskLot,
  KioskMachine,
  KioskOrder,
  KioskStage,
  PieceRate,
  WorkerLoginResponse,
  WorkerSession,
  WorkerSummary,
} from '../types/kiosk'

export interface ApiList<T> {
  data: T[]
  meta?: {
    page?: number
    current_page?: number
    per_page?: number
    total?: number
    total_pages?: number
    last_page?: number
  }
}

export interface ApiItem<T> {
  data: T
}

export function useKioskApi() {
  const client = useApiClient()

  async function listActiveOrders(): Promise<KioskOrder[]> {
    const response = await client.get<ApiList<KioskOrder>>('/production-orders', {
      params: { status: 'in_progress', page: 1, per_page: 100 },
    })
    return response.data.data
  }

  async function listStages(orderId: number): Promise<KioskStage[]> {
    const response = await client.get<ApiList<KioskStage>>(`/production-orders/${orderId}/stages`)
    return response.data.data
  }

  async function listLots(orderId: number): Promise<KioskLot[]> {
    const response = await client.get<ApiList<KioskLot>>(`/production-orders/${orderId}/lots`)
    return response.data.data
  }

  async function recordProgress(
    orderId: number,
    lotNumber: string,
    stageCode: string,
    payload: {
      completed_delta: number
      rejected_delta: number
      rework_delta: number
      employee_id: number
      notes?: string
    },
  ): Promise<void> {
    await client.post<ApiItem<unknown>>(
      `/production-orders/${orderId}/lots/${encodeURIComponent(lotNumber)}/stages/${encodeURIComponent(stageCode)}/progress`,
      payload,
    )
  }

  async function closeStage(
    orderId: number,
    lotNumber: string,
    stageCode: string,
    payload: { employee_id: number; notes?: string },
  ): Promise<void> {
    await client.post<ApiItem<unknown>>(
      `/production-orders/${orderId}/lots/${encodeURIComponent(lotNumber)}/stages/${encodeURIComponent(stageCode)}/close`,
      payload,
    )
  }

  async function listDowntimeReasons(): Promise<KioskDowntimeReason[]> {
    const response = await client.get<ApiList<KioskDowntimeReason>>('/production-execution/downtime-reasons')
    return response.data.data
  }

  async function listMachines(): Promise<KioskMachine[]> {
    const response = await client.get<ApiList<KioskMachine>>('/production-execution/machines', {
      params: { page: 1, per_page: 100 },
    })
    return response.data.data
  }

  /** Equipment catalog (reference items) used by the downtime machine picker. */
  async function listEquipment(): Promise<EquipmentItem[]> {
    const response = await client.get<ApiList<EquipmentItem>>('/reference-items/equipment', {
      params: { is_active: 1 },
    })
    return response.data.data
  }

  async function openDowntime(payload: {
    reason_code: string
    machine_id?: number | null
    order_id?: number | null
    lot_number?: string | null
    start_time: string
    notes?: string
  }): Promise<void> {
    await client.post<ApiItem<unknown>>('/production-execution/downtime', payload)
  }

  async function listProductionEmployees(): Promise<KioskEmployee[]> {
    const response = await client.get<ApiList<KioskEmployee>>('/employees', {
      params: { employee_type: 'production', is_active: 1, page: 1, per_page: 100 },
    })
    return response.data.data
  }

  async function fetchDailyOutput(date: string): Promise<KioskDailyOutput> {
    const response = await client.get<ApiItem<KioskDailyOutput>>('/production-execution/daily-output', {
      params: { date },
    })
    return response.data.data
  }

  /** Total completed/rejected/rework across the given rows. */
  function summarizeRows(rows: KioskDailyOutputRow[]) {
    return rows.reduce(
      (acc, row) => ({
        completed: acc.completed + Number(row.completed_quantity || 0),
        rejected: acc.rejected + Number(row.rejected_quantity || 0),
        rework: acc.rework + Number(row.rework_quantity || 0),
      }),
      { completed: 0, rejected: 0, rework: 0 },
    )
  }

  // --- Worker session endpoints ---

  async function workerLogin(payload: { card_uid?: string; pin?: string }): Promise<WorkerLoginResponse> {
    const response = await client.post<ApiItem<WorkerLoginResponse>>('/kiosk/worker-login', payload)
    return response.data.data
  }

  async function startSession(payload: {
    employee_id: number
    order_id: number
    client_event_id: string
    shift: string
    lot_number: string
    stage_code: string
    started_at: string
  }): Promise<WorkerSession> {
    const response = await client.post<ApiItem<WorkerSession>>('/kiosk/sessions', payload)
    return response.data.data
  }

  async function pauseSession(sessionId: number, payload: { paused_at: string; client_event_id: string }): Promise<WorkerSession> {
    const response = await client.post<ApiItem<WorkerSession>>(`/kiosk/sessions/${sessionId}/pause`, payload)
    return response.data.data
  }

  async function resumeSession(sessionId: number, payload: { resumed_at: string; client_event_id: string }): Promise<WorkerSession> {
    const response = await client.post<ApiItem<WorkerSession>>(`/kiosk/sessions/${sessionId}/resume`, payload)
    return response.data.data
  }

  async function finishSession(
    sessionId: number,
    payload: {
      finished_at: string
      completed_delta: number
      rejected_delta: number
      rework_delta: number
      client_event_id: string
    },
  ): Promise<WorkerSession> {
    const response = await client.post<ApiItem<WorkerSession>>(`/kiosk/sessions/${sessionId}/finish`, payload)
    return response.data.data
  }

  async function fetchWorkerSummary(employeeId: number, date: string): Promise<WorkerSummary> {
    const response = await client.get<ApiItem<WorkerSummary>>('/kiosk/me/summary', {
      params: { employee_id: employeeId, date },
    })
    return response.data.data
  }

  async function fetchPieceRates(params: {
    stage_code: string
    product_id?: number
    employee_id?: number
    shift?: 'day' | 'night'
  }): Promise<PieceRate[]> {
    const response = await client.get<ApiList<PieceRate>>('/production-execution/piece-rates', {
      params,
    })
    return response.data.data
  }

  return {
    listActiveOrders,
    listStages,
    listLots,
    recordProgress,
    closeStage,
    listDowntimeReasons,
    openDowntime,
    listProductionEmployees,
    fetchDailyOutput,
    summarizeRows,
    workerLogin,
    startSession,
    pauseSession,
    resumeSession,
    finishSession,
    fetchWorkerSummary,
    fetchPieceRates,
    listMachines,
    listEquipment,
  }
}

export type KioskApi = ReturnType<typeof useKioskApi>
